import React, { FC } from "react";
import { GivingActivity } from "@shared/models";

import { dayFullLabel, dayLabel, dayPillState, resolveTodayIndex } from "./givingWeek";
import { barHeight, barLabel, monthFullLabel } from "./monthBars";

import "./index.scss";

interface GivingActivitySectionProps {
  activity: GivingActivity;
  // The SAME value `MySparksContainer` sent as the request's `tzOffsetMinutes` (captured once at
  // mount) -- see `givingWeek.ts`'s own comment for why reusing it, rather than reading the clock a
  // second time, is what makes "today" agree with the server's own bucketing by construction.
  tzOffsetMinutes: number;
  // Passed down rather than read via `new Date()` inside this component, so the timezone-sensitive
  // render is exercised directly from a test-controlled instant, with no system-clock mocking needed.
  now: Date;
}

/**
 * "Your giving week" -- the Overview screen's last block (overview-rebuild-pr5-plan.md), rendered as
 * the final child of `.my-sparks-grid-main`. Backed by `GET /recognitions/giving-activity`.
 * Presentational: `MySparksContainer` owns the fetch and the loading/failure notes, and captures
 * `tzOffsetMinutes` once per mount (`now` is passed fresh at every render -- see
 * `MySparksContainer.tsx`'s own comment on that line); this component only renders what it's given.
 *
 * No date parsing happens here -- `resolveTodayIndex`/`dayLabel`/`barLabel` (the pure leaves in
 * `givingWeek.ts`/`monthBars.ts`) do all of it, so jest can exercise the timezone-sensitive logic
 * directly, without a DOM (layout assertions are vacuous in this repo's jest -- SCSS is stubbed and
 * `getBoundingClientRect()` returns zeros).
 *
 * The dot renders as a plain filled circle -- no per-dot glyph asset exists in this repo (this
 * directory's own plan doc, following `SparksCounters`'/`FlameProgress`'s established precedent for
 * exactly this gap), so colour is what carries the meaning -- an `aria-label` on the dot/bar itself
 * (review fix, C3) carries the same data as a non-colour channel, since a screen reader gets no
 * information at all from a class name alone.
 *
 * Named `GivingActivitySection`, matching `BadgesSection`'s own suffix convention (review fix, D6) --
 * `GivingActivity` alone collides with the wire type of the same name exported from
 * `@shared/models`, which used to force this file to import it under an alias.
 */
const GivingActivitySection: FC<GivingActivitySectionProps> = (props) => {
  const { activity, tzOffsetMinutes, now } = props;
  const { budget, usedTrailing7Days, days, months } = activity;

  const todayIndex = resolveTodayIndex(days, now, tzOffsetMinutes);
  const maxMonthTotal = Math.max(...months.map((month) => month.total));

  return (
    <div className="giving-activity-section" data-testid="giving-activity-section">
      <div className="giving-activity-section-head">
        <span className="giving-activity-section-title">Your giving week</span>
        {/* SPARK/CardHead's own documentation: an arrow means the head navigates. This right slot is
            metadata, never a link -- no arrow, no click target (see this directory's plan doc, "The
            head's right slot is a caption, not a link"). Review fix A: the Figma copy ("N of M weekly
            budget used") reads as if it were scoped to the calendar week strip directly below it, but
            `usedTrailing7Days` is a ROLLING 168-hour sum, not the Mon-Sun week the strip renders -- on
            a Monday the two would otherwise look like they measure the same window while one has just
            reset and the other hasn't. Naming the window explicitly avoids that false impression. */}
        <span className="giving-activity-section-caption">{`${usedTrailing7Days} of ${budget} used in the last 7 days`}</span>
      </div>
      <div className="giving-activity-section-week">
        {days.map((day, index) => {
          const isToday = index === todayIndex;
          const state = dayPillState(day, isToday);
          return (
            <div
              key={day.date}
              data-testid={`giving-activity-section-day-pill-${day.date}`}
              className={`giving-activity-section-day-pill${isToday ? " giving-activity-section-day-pill--today" : ""}`}
            >
              <span className="giving-activity-section-day-label">{dayLabel(day.date)}</span>
              <span
                className={`giving-activity-section-day-dot giving-activity-section-day-dot--${state}`}
                data-testid={`giving-activity-section-day-dot-${day.date}`}
                // Review fix C3: colour alone used to carry all three states; a screen reader read
                // nothing at all off an empty <span>. `dayFullLabel`, not the abbreviated `dayLabel`
                // used for the visible pill text, so the accessible name reads as a whole word.
                aria-label={`${dayFullLabel(day.date)}: ${day.total > 0 ? `${day.total} Sparks given` : "none given"}`}
              />
            </div>
          );
        })}
      </div>
      <div className="giving-activity-section-rule" />
      <div className="giving-activity-section-months-caption">Sparks you&apos;ve given · past 12 months</div>
      <div className="giving-activity-section-months">
        {months.map((month) => {
          const empty = month.total === 0;
          return (
            <div
              className="giving-activity-section-month"
              key={month.month}
              data-testid={`giving-activity-section-month-${month.month}`}
            >
              <div className="giving-activity-section-month-bar-area">
                <div
                  className={`giving-activity-section-month-bar${
                    empty ? " giving-activity-section-month-bar--empty" : ""
                  }`}
                  style={{ height: `${barHeight(month.total, maxMonthTotal)}px` }}
                  // Review fix C3: height alone (3px floor) doesn't distinguish a real zero month from
                  // a merely-tiny one -- only colour did, and colour isn't available to a screen reader.
                  aria-label={`${monthFullLabel(month.month)}: ${
                    month.total > 0 ? `${month.total} Sparks given` : "none given"
                  }`}
                />
              </div>
              <span className="giving-activity-section-month-label">{barLabel(month.month)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GivingActivitySection;
